import { Injectable, inject } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ConditionalService,
  isFormSelectField,
} from '@fmnts/declarative-forms';
import { CampaignForm } from '@formunauts/shared/campaigning/domain';

/**
 * Service providing utilities for working with fields.
 */
@Injectable({
  providedIn: 'root',
})
export class FormFieldService {
  private readonly conditionalService = inject(ConditionalService);

  /**
   * Filters the items of a given `field` based on some given `predicate`.
   *
   * @param field Field containing the items
   * @param predicate
   *
   * @returns
   * The first item in `field.items` that fulfilles the provided predicate
   */
  public findItemByPredicate(
    field: CampaignForm.SingleSelectField | CampaignForm.MultiSelectField,
    predicate: (value: unknown) => boolean,
  ): unknown | undefined {
    // Check for the queried value
    if (!predicate) {
      return undefined;
    }

    // Check for field props
    if (!field || !Array.isArray(field.widgetOptions.items)) {
      return undefined;
    }

    return field.widgetOptions.items.find(
      ({ value }) => value && predicate(value),
    );
  }

  /**
   * @param field
   *
   * @param formValues If formValues are passed, it will perform conditional
   * checks.
   *
   * @returns
   * An array of values that are allowed for this field.
   * In case that there are no value restrictions, `null` is returned.
   */
  public getAllowedValues(
    field: CampaignForm.Field,
    formValues?: Record<string, unknown>,
  ): unknown[] | null {
    if (isFormSelectField(field)) {
      const { itemConditions, items } = field.widgetOptions;

      if (!itemConditions || !formValues) {
        return items.map((i: { value: unknown }) => i.value);
      }

      return items
        .filter((i: { value: string | number }) =>
          itemConditions[i.value]
            ? this.conditionalService.checkObj(
                formValues,
                itemConditions[i.value],
              )
            : true,
        )
        .map((i: { value: unknown }) => i.value);
    }

    return null;
  }
}
